import React, {useMemo, useState} from "react";
import Modal from "react-modal";
import Files from "react-files";
import papa from "papaparse";
import {toast} from "react-toastify";
import PropTypes from "prop-types";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import ReactTable from "../../Components/ReactTable";
import {makeApiCallPost} from "../../../Api/makeApiCall";
import {setValuesToNullIfEmptyOrUndefined} from "../../Components/ReactTable/helpers";
import {toastError, toastSuccess} from "../../../utils/toast";

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const PREVIEW_COLUMNS = [
    {header: "Store ID", accessorKey: "storeId"},
    {header: "Store Name", accessorKey: "storeName"},
    {header: "Stop Date", accessorKey: "stopDate"},
    {header: "Start Date", accessorKey: "startDate"}
];

function parseRow(row, rowNumber) {
    const storeId = row.storeId?.trim();
    const stopDate = row.stopDate?.trim();
    const startDate = row.startDate?.trim();
    const errors = [];

    if (!storeId || isNaN(Number(storeId))) {
        errors.push(`Row ${rowNumber}: storeId is required and must be a number`);
    }
    if (!stopDate || !DATE_PATTERN.test(stopDate)) {
        errors.push(`Row ${rowNumber}: stopDate is required and must be in YYYY-MM-DD format`);
    }
    if (startDate && !DATE_PATTERN.test(startDate)) {
        errors.push(`Row ${rowNumber}: startDate must be in YYYY-MM-DD format`);
    }

    return {row: {storeId: Number(storeId), stopDate, startDate: startDate || null}, errors};
}

function StoreParticipationBulkUpload({onSubmitted, storeNamesById = {}}) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [step, setStep] = useState('selectFile');
    const [parsedRows, setParsedRows] = useState([]);
    const [parseErrors, setParseErrors] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const previewRows = useMemo(() => parsedRows.map(row => ({
        ...row,
        storeName: storeNamesById[row.storeId]
    })), [parsedRows, storeNamesById]);

    function closeAndReset() {
        setIsModalOpen(false);
        setStep('selectFile');
        setParsedRows([]);
        setParseErrors([]);
    }

    function onFileSelected(files) {
        papa.parse(files[0], {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const rows = [];
                const errors = [];
                // Row 1 is the header; papaparse's results.data is 0-indexed after that.
                results.data.forEach((rawRow, index) => {
                    const {row, errors: rowErrors} = parseRow(rawRow, index + 2);
                    if (rowErrors.length > 0) {
                        errors.push(...rowErrors);
                    } else {
                        rows.push(row);
                    }
                });
                setParsedRows(rows);
                setParseErrors(errors);
                setStep('preview');
            }
        });
    }

    async function submitRows() {
        setSubmitting(true);
        const responses = [];
        // Submitted one row at a time (not multiple_submits), the same request shape Bulk Add uses --
        // each uploaded row always creates a new participation window.
        for (const row of parsedRows) {
            responses.push(await makeApiCallPost(
                `${process.env.REACT_APP_EDP_API_BASE_URL}/storeParticipation/?single_row_action=update`,
                setValuesToNullIfEmptyOrUndefined({...row})
            ));
        }
        setSubmitting(false);
        if (responses.some(response => response?.apiError)) {
            toastError(toast, `Operation failed with api error "${JSON.stringify(responses.find(response => response?.apiError)?.apiError.message)}"`);
            return;
        }
        toastSuccess(toast, `Successfully imported ${parsedRows.length} row/s.`);
        closeAndReset();
        onSubmitted();
    }

    return <>
        <button type={"button"} className={"button tertiary"}
                data-testid={"store-participation-bulk-upload-button"}
                onClick={() => setIsModalOpen(true)}>
            Bulk Upload
        </button>
        <Modal
            clickable
            isOpen={isModalOpen}
            className={"react-table-edit-modal store-participation-bulk-upload-modal"}
            overlayClassName="Overlay"
            ariaHideApp={false}
            onRequestClose={closeAndReset}
            shouldCloseOnOverlayClick={false}
        >
            <h5 className={"modal-header-text"}>Bulk Upload Store Participation</h5>
            {step === 'selectFile' && <div>
                <div>File should be a .csv with columns: storeId, stopDate, startDate (optional).</div>
                <div>Each row creates a new participation window &mdash; it does not edit existing rows.</div>
                <div className={'store-participation-bulk-upload-actions'}>
                    <button type={'button'} className={'button cancel'} onClick={closeAndReset}>Cancel</button>
                    <Files
                        className={"button primary store-participation-browse-button"}
                        onChange={onFileSelected}
                        accepts={['.csv']}
                        multiple={false}
                        maxFileSize={10 * 1024 * 1024}
                        clickable
                    >
                        Browse...
                    </Files>
                </div>
            </div>}
            {step === 'preview' && <LoadingWrapper loading={submitting}>
                {parseErrors.length > 0 && <div data-testid={'bulk-upload-parse-errors'}>
                    <div>{parseErrors.length} row/s could not be parsed and will be skipped:</div>
                    <textarea readOnly title={'Parse Errors'} defaultValue={parseErrors.join('\n')}/>
                </div>}
                <div data-testid={'bulk-upload-valid-row-count'}>{parsedRows.length} valid row/s ready to import</div>
                {parsedRows.length > 0 && <ReactTable
                    data={previewRows}
                    columns={PREVIEW_COLUMNS}
                    initialState={{pagination: {pageSize: 10}}}
                    showPageSizeDropdown={false}
                />}
                <div className={'store-participation-bulk-upload-actions'}>
                    <button type={'button'} className={'button cancel'} onClick={closeAndReset}>Cancel</button>
                    <button type={'button'} className={'button primary'}
                            data-testid={'bulk-upload-import-button'}
                            disabled={parsedRows.length === 0 || submitting}
                            onClick={submitRows}>
                        {`Import ${parsedRows.length} row/s`}
                    </button>
                </div>
            </LoadingWrapper>}
        </Modal>
    </>;
}

StoreParticipationBulkUpload.propTypes = {
    onSubmitted: PropTypes.func.isRequired,
    storeNamesById: PropTypes.object
};

export default StoreParticipationBulkUpload;
